import React, { useState, useEffect } from "react";

import { getJson } from "../network/api";
import { ApiEndpoints } from "../network/config";
import "../styles/MyProfile.css";

const Profile = () => {
  const [name, setName] = useState(() => {
    try {
      return sessionStorage.getItem("userName") || "User";
    } catch {
      return "User";
    }
  });

  const [activeTab, setActiveTab] = useState("basic");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [profileImages, setProfileImages] = useState([]); 


  const [data, setData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

 
  const calculateAge = (dobStr) => {
    if (!dobStr) return "";
    const parts = dobStr.split("-");
    if (parts.length !== 3) return "";
    const day = Number(parts[0]);
    const month = Number(parts[1]) - 1;
    const year = Number(parts[2]);
    const birth = new Date(year, month, day);
    return Math.floor((Date.now() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };


  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === profileImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? profileImages.length - 1 : prevIndex - 1
    );
  };


  const mapApiToUi = (api) => ({
    basic: {
      gender: api.gender ?? "",
      maritalStatus: api.personalDetails?.maritalStatus ?? "",
      age: api.birthDetails?.dob ? calculateAge(api.birthDetails.dob) : "",
      mobileNumber: api.contactDetails?.mobileNo1 ?? "",
      caste: api.caste ?? "",
      subcaste: api.subcaste ?? "",
      bedagu: api.bedagu ?? "",
    },
    personal: {
      height: api.personalDetails?.height ?? "",
      weight: api.personalDetails?.weight ?? "",
      bloodGroup: api.personalDetails?.bloodGroup ?? "",
      bodyType: api.personalDetails?.bodyType ?? "",
      complexion: api.personalDetails?.complexion ?? "",
    },
    birth: {
      dob: api.birthDetails?.dob ?? api.birthDetails?.Dob ?? "",
      birthPlace: api.birthDetails?.birthPlace ?? "",
      birthTime: api.birthDetails?.birthTime ?? "",
      moonSign: api.birthDetails?.moonsign ?? api.birthDetails?.moonSign ?? "",
      star: api.birthDetails?.star ?? "",
      gothra: api.birthDetails?.gothra ?? "",
      pada: api.birthDetails?.pada ?? "",
    },
    family: {
      father: api.familyDetails?.fatherName ?? "",
      fatherOccupation: api.familyDetails?.fatherOccupation ?? "",
      mother: api.familyDetails?.motherName ?? "",
      motherOccupation: api.familyDetails?.motherOccupation ?? "",
      brothers: api.familyDetails?.noOfBrothers ?? "",
      marriedBrothers: api.familyDetails?.noOfBrothersMarried ?? "",
      sisters: api.familyDetails?.noOfSisters ?? "",
      marriedSisters: api.familyDetails?.noOfSistersMarried ?? "",
    },
    contact: {
      district: api.contactDetails?.district ?? "",
      residentAt: api.contactDetails?.residentAt ?? "",
      contactNo: api.contactDetails?.mobileNo1 ?? "",
    },
    education: {
      stream: api.educationsDetails?.educationStream ?? "",
      qualification: api.educationsDetails?.qualification ?? "",
      occupation: api.educationsDetails?.occupation ?? "",
      workingIn: api.educationsDetails?.workingIn ?? "",
      annualIncome: api.educationsDetails?.annualIncome ?? "",
      jobType: api.educationsDetails?.jobType ?? "",
    },
    looking: {
      age:
        api.lookingForDetails?.ageFrom && api.lookingForDetails?.ageTo
          ? `${api.lookingForDetails.ageFrom} to ${api.lookingForDetails.ageTo}`
          : "",
      caste: api.lookingForDetails?.caste ?? "",
      subcaste: api.lookingForDetails?.subCaste ?? "",
      height: api.lookingForDetails?.height ?? "",
      qualification: api.lookingForDetails?.qualification ?? "",
      occupation: api.lookingForDetails?.occupation ?? "",
      annualIncome: api.lookingForDetails?.annualIncome ?? "",
      maritalStatus: api.lookingForDetails?.maritalStatus ?? "",
    },
  });

  
  useEffect(() => {
    const userId = sessionStorage.getItem("userId");

    if (!userId) {
      setProfileError("User ID missing");
      setLoadingProfile(false);
      return;
    }

    const fetchData = async () => {
      try {
        const res = await getJson(`${ApiEndpoints.myProfile}/${userId}`);
        if (res?.data) {
          const api = res.data;

          // Update Name
          setName(api.name || "User");

          // Update Images
          if (api.profileImages?.length > 0) {
            setProfileImages(api.profileImages.map((i) => i.imageUrl));
          }

          // Map to UI structure
          setData(mapApiToUi(api));
        }
      } catch (err) {
        console.error(err);
        setProfileError("Failed to load profile");
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchData();
  }, []);

  // -----------------------------------------
  // Field lists per tab
  // -----------------------------------------
  const getFieldsForTab = (tab) => {
    const map = {
      basic: [
        ["gender", "Gender"],
        ["maritalStatus", "Marital Status"],
        ["age", "Age"],
        ["mobileNumber", "Mobile Number"],
        ["caste", "Caste"],
        ["subcaste", "Subcaste"],
        ["bedagu", "Bedagu"],
      ],
      personal: [
        ["height", "Height"],
        ["weight", "Weight"],
        ["bloodGroup", "Blood Group"],
        ["bodyType", "Body Type"],
        ["complexion", "Complexion"],
      ],
      birth: [
        ["dob", "Date of Birth"],
        ["birthPlace", "Birth Place"],
        ["birthTime", "Birth Time"],
        ["moonSign", "Moon Sign"],
        ["star", "Star"],
        ["gothra", "Gothra"],
        ["pada", "Pada"],
      ],
      family: [
        ["father", "Father"],
        ["fatherOccupation", "Father Occupation"],
        ["mother", "Mother"],
        ["motherOccupation", "Mother Occupation"],
        ["brothers", "Brothers"],
        ["marriedBrothers", "Married Brothers"],
        ["sisters", "Sisters"],
        ["marriedSisters", "Married Sisters"],
      ],
      contact: [
        ["district", "District"],
        ["residentAt", "Resident At"],
        ["contactNo", "Contact No"],
      ],
      education: [
        ["stream", "Education Stream"],
        ["qualification", "Qualification"],
        ["occupation", "Occupation"],
        ["workingIn", "Working In"],
        ["annualIncome", "Annual Income"],
        ["jobType", "Job Type"],
      ],
      looking: [
        ["age", "Age"],
        ["caste", "Caste"],
        ["subcaste", "Subcaste"],
        ["height", "Height"],
        ["qualification", "Qualification"],
        ["occupation", "Occupation"],
        ["annualIncome", "Annual Income"],
        ["maritalStatus", "Marital Status"],
      ],
    };

    return map[tab] ?? [];
  };

  // -----------------------------------------
  // Render Tab Data
  // -----------------------------------------
  const renderDetailsForTab = (tab) => {
    if (!data) return <p>Loading...</p>;
    const fields = getFieldsForTab(tab);
    const current = data[tab];

    return (
      <div className="my-details-list">
        {fields.map(([key, label]) => (
          <div className="my-detail-row" key={key}>
            <span>{label}</span>
            <span>{current[key] || "—"}</span>
          </div>
        ))}
      </div>
    );
  };

  // -----------------------------------------
  // RENDER UI
  // -----------------------------------------
  return (
    <div className="my-profile-container">
      <h1 className="my-profile-title">My Profile</h1>
      <p className="my-profile-subtitle">
        View your personal and matrimonial details.
      </p>

      <div className="my-profile-content">
        {/* LEFT SIDE */}
        <div className="my-profile-left">
          <h2 className="my-name">{name}</h2>

          <div className="my-image-slider">
            <img
              src={profileImages[currentImageIndex]}
              alt="Profile"
              className="my-profile-image"
            />

            {profileImages.length > 1 && (
              <>
                <button className="my-slider-btn my-prev-btn" onClick={prevImage}>
                  ‹
                </button>
                <button className="my-slider-btn my-next-btn" onClick={nextImage}>
                  ›
                </button>
              </>
            )}

            {profileImages.length > 1 && (
              <div className="my-image-indicators">
                {profileImages.map((_, index) => (
                  <button
                    key={index}
                    className={`my-indicator ${
                      index === currentImageIndex ? "my-active-indicator" : ""
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="my-profile-right">
          <div className="my-tabs">
            {["basic", "personal", "birth", "family", "contact", "education", "looking"].map(
              (tab) => (
                <button
                  key={tab}
                  className={`my-tab ${activeTab === tab ? "my-active-tab" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "looking"
                    ? "Looking For"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              )
            )}
          </div>

          <div style={{ paddingTop: 8 }}>
            {activeTab === "basic" && <h3>Basic Details</h3>}
            {activeTab === "personal" && <h3>Personal Details</h3>}
            {activeTab === "birth" && <h3>Birth Details</h3>}
            {activeTab === "family" && <h3>Family Details</h3>}
            {activeTab === "contact" && <h3>Contact Details</h3>}
            {activeTab === "education" && <h3>Education & Profession</h3>}
            {activeTab === "looking" && <h3>Looking For</h3>}

            {renderDetailsForTab(activeTab)}

            {/* DO NOT REMOVE (as requested) */}
            {/*
            <div className="my-buttons">
              <button className="my-cancel-btn">Cancel</button>
              <button className="my-update-btn">Update</button>
            </div>
            */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
